import { Handle, NodeProps, Position, useConnection } from '@xyflow/react';
import { NodesTypeData } from './EditProcessMap';
import { useFloatNotification } from 'contexts/FloatNotificationContext';
import { useTranslation } from 'react-i18next';
import { useUpdateStatusCanvasProcessMapByID } from 'api/services/processMapService';
import { useUI } from 'contexts/UiContext';
import { ErrorType } from 'types/genericTypes';
import { CustomNodeContent } from './CustomNodeContent';
import { useCallback } from 'react';
import { Tooltip } from 'antd';
import { DarwinFlex } from 'components/darwin-ui';

export const CustomNode = ({
  data,
  id,
}: { data: NodesTypeData } & NodeProps) => {
  const { openNotification } = useFloatNotification();
  const { selectedCompany } = useUI();
  const { t } = useTranslation();
  const { mutateAsync: updateStatusNode, isPending: isUpdatingPending } =
    useUpdateStatusCanvasProcessMapByID();

  const connection = useConnection();

  const isTarget = connection.inProgress && connection.fromNode.id !== id;

  const updateStatus = useCallback(
    async (status: string, newStatus: string) => {
      if (status === newStatus) {
        return openNotification(
          t('warning'),
          t('process-map.same-status-error', { status }),
          'warning',
          'alert'
        );
      }

      try {
        await updateStatusNode({
          companyId: selectedCompany || '',
          planId: data.processMapId || '',
          processFlowId: data.newNodeId || '',
          payload: { status: newStatus },
        });
        openNotification(
          t('success'),
          t('process-map.update-status-success'),
          'success'
        );
      } catch (err) {
        const apiError = err as ErrorType;
        openNotification(
          t('error'),
          apiError.body?.errorCode
            ? t(apiError.body.errorCode)
            : t('process-map.update-status-error'),
          'error'
        );
      }
    },
    [openNotification, selectedCompany, data, t, updateStatusNode]
  );

  return (
    <>
      {/* handle for input (target) */}
      <Handle
        style={{
          width: 12,
          height: 12,
        }}
        type="target"
        position={Position.Top}
        isConnectable={true}
      />

      {/* invisible handle for connection on the node body */}
      {(!connection.inProgress || isTarget) && (
        <Handle
          style={{
            width: '100%',
            height: '100%',
            background: 'blue',
            position: 'absolute',
            top: 0,
            left: 0,
            borderRadius: 0,
            transform: 'none',
            border: 'none',
            opacity: 0,
          }}
          position={Position.Left}
          type="target"
          isConnectableStart={false}
        />
      )}

      {/* main node container */}
      {data.processDescription && !data.isDragging ? (
        <Tooltip title={data.processDescription} placement="top">
          <DarwinFlex style={{ width: '100%' }}>
            <CustomNodeContent
              data={data}
              isUpdatingPending={isUpdatingPending}
              updateStatus={updateStatus}
            />
          </DarwinFlex>
        </Tooltip>
      ) : (
        <CustomNodeContent
          data={data}
          isUpdatingPending={isUpdatingPending}
          updateStatus={updateStatus}
        />
      )}
      {/* handle for output (source) */}
      <Handle
        style={{
          width: 12,
          height: 12,
        }}
        type="source"
        position={Position.Bottom}
        isConnectable={true}
      />
    </>
  );
};
